import { useState } from 'react';

import TextAreaPrimitive from '@/components/ui/TextArea';
import { cn } from '@/lib/utils';

import {
  fieldErrorClass,
  FIELD_ERROR_PLACEHOLDER_CLASS,
  textAppearanceClass,
  type TextAppearance,
  type TextOutline,
} from '../../field/textAppearance';
import { useCardMetrics } from '../../internals/useCardMetrics';
import { TEXT_BOX_CLASS, TEXT_STYLE, type TextStyleName } from '../../tokens';

export type EditorProps = {
  id: string;
  value: string;
  placeholder: string | undefined;
  error: string | undefined;
  appearance: TextAppearance;
  outline: TextOutline | undefined;
  textStyle: TextStyleName;
  // The field's name when no visible label names it
  ariaLabel?: string;
  autoFocus?: boolean;
  lines?: number;
  toneClass?: string;
  bodyClass?: string;
  onChange: (value: string) => void;
  onFocus: () => void;
  onBlur: () => void;
};

export const PlainEditor = ({
  id,
  value,
  placeholder,
  error,
  appearance,
  outline,
  textStyle,
  ariaLabel,
  autoFocus,
  lines,
  toneClass,
  bodyClass,
  onChange,
  onFocus,
  onBlur,
}: EditorProps) => {
  const [changed, setChanged] = useState(false);
  const metrics = useCardMetrics();
  return (
    <TextAreaPrimitive
      id={id}
      rows={1}
      value={value}
      placeholder={placeholder}
      autoFocus={autoFocus}
      aria-label={ariaLabel}
      error={error}
      className={cn(
        TEXT_BOX_CLASS,
        textAppearanceClass(appearance, outline),
        TEXT_STYLE[textStyle],
        bodyClass,
        toneClass,
        metrics.bodyMaxHeight,
        'min-h-[1lh] resize-none overflow-y-auto whitespace-pre-wrap py-0 [field-sizing:content] transition-[height] duration-150 motion-reduce:transition-none',
        // The font reaches a pixel past the last line; the padding keeps the box
        // from scrolling and the next row's gap starts below it
        'pb-px',
        error && fieldErrorClass(appearance, outline),
        error && FIELD_ERROR_PLACEHOLDER_CLASS.native
      )}
      style={
        !changed && lines ? { maxHeight: `calc(${lines}lh + 1px)` } : undefined
      }
      onChange={(event) => {
        setChanged(true);
        onChange(event.target.value);
      }}
      onFocus={() => {
        onFocus();
      }}
      onBlur={() => {
        setChanged(false);
        onBlur();
      }}
    />
  );
};
